import { User } from 'firebase/auth';
import { API_URL } from '../config';

export interface Webhook {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  createdAt: string | null;
}

export interface CreatedWebhook extends Webhook {
  /** The signing secret — returned only on creation; store it securely. */
  secret: string;
}

/** Fetch all webhooks for a project. */
export async function listWebhooks(user: User, projectId: string): Promise<Webhook[]> {
  const token = await user.getIdToken();
  const res = await fetch(`${API_URL}/webhooks/${projectId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Failed to list webhooks' }));
    throw new Error(body.error ?? 'Failed to list webhooks');
  }
  const data: { webhooks: Webhook[] } = await res.json();
  return data.webhooks;
}

/** Register a new webhook for a project. Returns the webhook including its signing secret. */
export async function createWebhook(
  user: User,
  projectId: string,
  url: string,
  events: string[] = ['subscriber.created'],
): Promise<CreatedWebhook> {
  const token = await user.getIdToken();
  const res = await fetch(`${API_URL}/webhooks/${projectId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ url, events }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Failed to create webhook' }));
    throw new Error(body.error ?? 'Failed to create webhook');
  }
  return res.json();
}

/** Delete a webhook. */
export async function deleteWebhook(
  user: User,
  projectId: string,
  webhookId: string,
): Promise<void> {
  const token = await user.getIdToken();
  const res = await fetch(`${API_URL}/webhooks/${projectId}/${webhookId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Failed to delete webhook' }));
    throw new Error(body.error ?? 'Failed to delete webhook');
  }
}
